import React, { useEffect, useId, useState } from 'react';
import { noop } from 'lodash-es';

import StateMessages from 'common/components/Input/StatefulInput/StateMessages';
import { FieldChangeTrigger, HTMLAutoCompleteAttribute } from 'common/enums/form';
import { useKeyboardSelect } from 'common/hooks/keyboard';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { FormGroupData } from 'common/types/field';
import type { FieldChangeTriggerHandler } from 'common/types/form';
import { useDrawer } from '../../hooks/drawer';
import { useFetchProducts } from '../../hooks/products-drawer';
import { getProductSummary, getProductThumbnails, LINE_NO_SEPARATOR } from '../../utils/products';
import { selectDrawerContentTrackingEvent } from '../../utils/tracking';
import Drawer from '../Fields/common/Drawer';
import { ProductList } from '../ProductDrawer/ProductList';
import DrawerInput from './common/DrawerInput';
import ProductThumbnails from './ProductThumbnails';

interface ProductNumberDrawerProps<FieldName extends string = string> {
  name: FieldName;
  label: string;
  value: string;
  required?: boolean;
  disabled?: boolean;
  orderId: string;
  reason: string;
  onChange: FieldChangeTriggerHandler<FieldName>;
  setDrawerError: () => void;
  hasError?: boolean;
  errors?: string[];
  isSellerContactMode?: boolean;
}

export type ProductNumberDrawerFormData = FormGroupData<ProductNumberDrawerProps>;

const ProductNumberDrawer = ({
  name,
  value = '',
  label,
  orderId,
  required,
  onChange,
  setDrawerError = noop,
  reason,
  hasError: hasErrorProp,
  errors,
  isSellerContactMode = false,
}: ProductNumberDrawerProps) => {
  const { t } = useLocalization();
  const stateMessagesId = useId();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const { products, isLoading } = useFetchProducts(orderId, setDrawerError);
  const { isOpen, openDrawer, closeDrawer } = useDrawer('item', reason);
  const onKeyDown = useKeyboardSelect(openDrawer);
  const productSummary = getProductSummary(value, products);
  const productThumbnails = getProductThumbnails(value, products);

  useEffect(() => {
    if (isSellerContactMode && !isLoading && products.length === 1 && !value) {
      onChange({
        fieldName: name,
        value: products[0].lineNo,
        trigger: FieldChangeTrigger.Change,
      });
    }
  }, [isSellerContactMode, isLoading, products, value, name, onChange]);

  const setProducts = (newValues: string[]) => {
    selectDrawerContentTrackingEvent(reason, 'item');
    onChange({
      fieldName: name,
      value: newValues.join(LINE_NO_SEPARATOR),
      trigger: FieldChangeTrigger.Change,
    });
  };

  useEffect(() => {
    // Select values that were already selected when drawer is opened
    if (isOpen && !!value) {
      setSelectedProducts(value.split(LINE_NO_SEPARATOR));
    }
  }, [isOpen]);

  const onBlur = () => {
    onChange({
      fieldName: name,
      value: value ?? '',
      trigger: FieldChangeTrigger.Blur,
    });
  };

  const hasError = !isOpen && hasErrorProp;
  const isReadOnly = !orderId;

  return (
    <>
      <DrawerInput
        name={name}
        label={label}
        onChange={noop}
        readOnly={isReadOnly}
        required={required}
        hasError={hasError}
        onBlur={isReadOnly ? undefined : onBlur}
        onClick={isReadOnly ? undefined : openDrawer}
        onKeyDown={isReadOnly ? undefined : onKeyDown}
        value={productSummary}
        inputMode="none"
        autoComplete={HTMLAutoCompleteAttribute.Off}
        {...(hasError
          ? {
              'aria-describedby': stateMessagesId,
            }
          : {})}
      />
      {hasError && <StateMessages id={stateMessagesId} errors={errors} hasError={hasError} />}
      <Drawer
        title={t('h24_select_product')}
        isOpen={isOpen}
        onClose={closeDrawer}
        content={
          <ProductList
            key={orderId}
            orderId={orderId}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
            products={products}
            isLoading={isLoading}
            onClose={closeDrawer}
            setProducts={setProducts}
          />
        }
      />
      {productThumbnails?.length > 0 && <ProductThumbnails images={productThumbnails} />}
    </>
  );
};

const OrderProductNumberDrawer = (props: ProductNumberDrawerProps) => (
  <ProductNumberDrawer key={props.orderId} {...props} />
);

export default OrderProductNumberDrawer;
